"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import NextLink from "next/link";
import { useParams as useNextParams, usePathname, useRouter } from "next/navigation";

/** Avoid `typeof window` — webpack DefinePlugin folds that to `"undefined" === "undefined"` on the server build. */
function getBrowserWindow() {
  const g = globalThis;
  return g && g.window && g.window.document ? g.window : null;
}

function isAbsoluteUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

function normalizeClientHref(value) {
  const win = getBrowserWindow();
  if (!isAbsoluteUrl(value) || !win) return value;
  try {
    const parsed = new URL(value);
    const localHosts = new Set(["localhost", "127.0.0.1"]);
    if (parsed.origin === win.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
    }
    if (localHosts.has(parsed.hostname) && localHosts.has(win.location.hostname)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
    }
  } catch {
    return value;
  }
  return value;
}

function isExternalHref(href) {
  const win = getBrowserWindow();
  if (!isAbsoluteUrl(href) || !win) return false;
  try {
    const parsed = new URL(href);
    return parsed.origin !== win.location.origin;
  } catch {
    return false;
  }
}

function toHref(to) {
  if (typeof to === "string") return normalizeClientHref(to);
  if (!to || typeof to !== "object") return "/";
  const pathname = typeof to.pathname === "string" ? to.pathname : "";
  const search = typeof to.search === "string" ? to.search : "";
  const hash = typeof to.hash === "string" ? to.hash : "";
  const href = `${pathname}${search}${hash}` || "/";
  if (href.includes("[object Object]")) return "/";
  return normalizeClientHref(href);
}

function toSearchString(value) {
  if (typeof value === "string") return value.startsWith("?") ? value : `?${value}`;
  if (value instanceof URLSearchParams) {
    const s = value.toString();
    return s ? `?${s}` : "";
  }
  if (Array.isArray(value)) {
    const s = new URLSearchParams(value).toString();
    return s ? `?${s}` : "";
  }
  if (value && typeof value === "object") {
    const params = new URLSearchParams();
    Object.entries(value).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        v.forEach((entry) => params.append(k, String(entry)));
      } else {
        params.set(k, String(entry));
      }
    });
    const s = params.toString();
    return s ? `?${s}` : "";
  }
  return "";
}

const LOCATION_STATE_KEY = "southcrete:rr-location-state";

function writeLocationState(href, state) {
  const win = getBrowserWindow();
  if (!win) return;
  try {
    if (state === undefined) {
      win.sessionStorage.removeItem(LOCATION_STATE_KEY);
      return;
    }
    win.sessionStorage.setItem(LOCATION_STATE_KEY, JSON.stringify({ href, state }));
  } catch {
    // Ignore quota / private-mode failures; callers still have URL + dedicated storage.
  }
}

function readLocationState(href) {
  const win = getBrowserWindow();
  if (!win) return null;
  try {
    const raw = win.sessionStorage.getItem(LOCATION_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.href !== href) return null;
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

const searchListeners = new Set();
let searchNotifyScheduled = false;

function notifySearchListeners() {
  // Next.js router.replace/push applies history updates inside useInsertionEffect.
  // Notifying useSyncExternalStore subscribers synchronously from the patched
  // history methods would schedule a React update during that phase.
  if (searchNotifyScheduled) return;
  searchNotifyScheduled = true;
  const flush = () => {
    searchNotifyScheduled = false;
    searchListeners.forEach((listener) => listener());
  };
  if (typeof queueMicrotask === "function") queueMicrotask(flush);
  else setTimeout(flush, 0);
}

function getClientSearch() {
  const win = getBrowserWindow();
  return win ? win.location.search || "" : "";
}

function subscribeSearch(listener) {
  searchListeners.add(listener);
  const win = getBrowserWindow();
  if (!win) {
    return () => searchListeners.delete(listener);
  }
  if (!win.__southcreteSearchPatched) {
    win.__southcreteSearchPatched = true;
    const wrapHistory = (method) => {
      const original = win.history[method];
      win.history[method] = function patchedHistory(...args) {
        const result = original.apply(this, args);
        notifySearchListeners();
        return result;
      };
    };
    wrapHistory("pushState");
    wrapHistory("replaceState");
  }
  win.addEventListener("popstate", notifySearchListeners);
  return () => {
    searchListeners.delete(listener);
    win.removeEventListener("popstate", notifySearchListeners);
  };
}

function currentHrefFrom(pathname, search) {
  const normalized = search && !search.startsWith("?") ? `?${search}` : search || "";
  return `${pathname}${normalized}`;
}

export function BrowserRouter({ children }) {
  return children;
}

export function Routes({ children }) {
  return children;
}

export function Route() {
  return null;
}

export function Link({ to, href: hrefProp, children, scroll = false, prefetch = false, ...props }) {
  const rawHref = toHref(to ?? hrefProp);
  const href = typeof rawHref === "string" && rawHref.includes("[object Object]") ? "/" : rawHref;
  const safeProps = { ...props };
  delete safeProps.href;
  delete safeProps.to;
  delete safeProps.scroll;
  delete safeProps.prefetch;

  if (isExternalHref(href)) {
    return (
      <a href={href} {...safeProps}>
        {children}
      </a>
    );
  }
  return (
    <NextLink href={href} scroll={scroll} prefetch={prefetch} {...safeProps}>
      {children}
    </NextLink>
  );
}

export function Navigate({ to, replace = false }) {
  const router = useRouter();
  const pathname = usePathname() || "/";
  const search = useSyncExternalStore(subscribeSearch, getClientSearch, () => "");
  const currentHref = useMemo(() => currentHrefFrom(pathname, search), [pathname, search]);
  const href = toHref(to);
  useEffect(() => {
    if (href === currentHref) return;
    writeLocationState(href, undefined);
    if (replace) router.replace(href, { scroll: false });
    else router.push(href, { scroll: false });
  }, [href, replace, router, currentHref]);
  return null;
}

function getWindowHref() {
  const win = getBrowserWindow();
  if (!win) return "";
  return `${win.location.pathname}${win.location.search}${win.location.hash || ""}`;
}

export function useNavigate() {
  const router = useRouter();
  return useCallback((to, options = {}) => {
    const href = toHref(to);
    if (!href || href === getWindowHref()) return;
    writeLocationState(href, options?.state);
    const nextPath = href.split(/[?#]/)[0];
    if (options?.replace) router.replace(href, { scroll: false });
    else router.push(href, { scroll: false });
    notifySearchListeners();
    const win = getBrowserWindow();
    if (!win) return;
    win.setTimeout(() => {
      if (win.location.pathname === nextPath) return;
      if (options?.replace) win.location.replace(href);
      else win.location.assign(href);
    }, 80);
  }, [router]);
}

export function useParams() {
  return useNextParams() || {};
}

export function useLocation() {
  const pathname = usePathname() || "/";
  const search = useSyncExternalStore(subscribeSearch, getClientSearch, () => "");
  const normalizedSearch = search && !search.startsWith("?") && search.length > 0 ? `?${search}` : search;
  const href = `${pathname}${normalizedSearch}`;
  const [state, setState] = useState(null);
  useEffect(() => {
    setState(readLocationState(href));
  }, [href]);
  return useMemo(
    () => ({
      pathname,
      search: normalizedSearch,
      hash: "",
      state,
      key: href,
    }),
    [pathname, normalizedSearch, href, state]
  );
}

export function useSearchParams() {
  const router = useRouter();
  const pathname = usePathname() || "/";
  const search = useSyncExternalStore(subscribeSearch, getClientSearch, () => "");
  const currentHref = useMemo(() => currentHrefFrom(pathname, search), [pathname, search]);
  const lastDispatchedHrefRef = useRef(currentHref);
  const routerRef = useRef(router);
  const pathnameRef = useRef(pathname);
  const currentHrefRef = useRef(currentHref);
  const paramsRef = useRef(null);
  routerRef.current = router;
  pathnameRef.current = pathname;
  currentHrefRef.current = currentHref;
  useEffect(() => {
    lastDispatchedHrefRef.current = currentHref;
  }, [currentHref]);
  const params = useMemo(() => {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    return new URLSearchParams(raw);
  }, [search]);
  paramsRef.current = params;
  const setSearchParams = useCallback((value, options = {}) => {
    const currentParams = paramsRef.current || new URLSearchParams();
    const nextSearch = toSearchString(
      typeof value === "function" ? value(new URLSearchParams(currentParams)) : value,
    );
    const nextHref = `${pathnameRef.current}${nextSearch}`;
    if (nextHref === currentHrefRef.current || nextHref === lastDispatchedHrefRef.current) return;
    lastDispatchedHrefRef.current = nextHref;
    const win = getBrowserWindow();
    const scrollY = win ? win.scrollY : 0;
    const navOpts = { scroll: false };
    if (options?.replace) routerRef.current.replace(nextHref, navOpts);
    else routerRef.current.push(nextHref, navOpts);
    notifySearchListeners();
    if (win) {
      win.scrollTo(0, scrollY);
      win.requestAnimationFrame(() => win.scrollTo(0, scrollY));
    }
  }, []);
  return [params, setSearchParams];
}
